import { Box, Typography } from '@mui/material';
import Icon from './Icon';
import { BRAND } from '../shared/theme/paletteDark';
import { FONTS } from '../shared/theme/typography';
import { CG_RATIO, GAP_RATIO } from '../shared/utils/constants';

interface Props {
  /**
   * `phone`: the wordmark, "by" and the tree, all a way into About. `narrow`
   * (600–899px): the wordmark alone. `wide`: the whole lockup, from 900px up.
   */
  variant: 'phone' | 'narrow' | 'wide';
  /** The wordmark's width, as any CSS length. Everything else is a multiple of it. */
  size: string;
  onHome: () => void;
  onAbout: () => void;
}

/** "My Climate Week" by [CommonGround], as one unit. */
export default function Lockup({ variant, size, onHome, onAbout }: Props) {
  const phone = variant === 'phone';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        overflow: 'hidden',
        // Everything below is a multiple of this, so the three parts cannot
        // drift out of proportion with each other.
        '--lockup': size,
        // The phone lockup sets its own spacing: the ratio that keeps the
        // three pieces in proportion on a laptop leaves a 25px hole here.
        gap: phone ? '7px' : `calc(var(--lockup) * ${GAP_RATIO})`,
      }}
    >
      <Box
        component="button"
        type="button"
        /**
         * On a phone the lockup is the way into About.
         *
         * There is no room in that header for two text links, and "About
         * this project" is the one thing a first-time visitor needs from
         * it — the reset that this tap does on wider screens is a tap on
         * "Reset all filters", one pull of the sheet away.
         */
        onClick={phone ? onAbout : onHome}
        aria-label={
          phone
            ? 'My Climate Week by CommonGround — about this project'
            : 'My Climate Week — back to all events'
        }
        sx={{
          width: 'var(--lockup)',
          color: BRAND.parchment,
          flexShrink: 0,
          appearance: 'none',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          transition: 'opacity 120ms ease',
          '&:hover': { opacity: 0.75 },
        }}
      >
        <Icon name="logo:mcw" size="100%" title="My Climate Week" />
      </Box>
      {phone && (
        <>
          <Typography
            sx={{ fontSize: 11, fontWeight: 700, opacity: 0.6, fontFamily: FONTS.body }}
          >
            by
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={onAbout}
            aria-label="About this project"
            sx={{
              width: 30,
              color: BRAND.parchment,
              flexShrink: 0,
              appearance: 'none',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            <Icon name="logo:cgMark" size="100%" title="CommonGround" />
          </Box>
        </>
      )}
      {variant === 'wide' && (
        <>
          <Typography sx={{ fontSize: 15, fontWeight: 700, opacity: 0.6, fontFamily: FONTS.body }}>
            by
          </Typography>
          <Box
            component="a"
            href="https://www.commonground.boston/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="CommonGround (opens in a new tab)"
            sx={{
              width: `calc(var(--lockup) * ${CG_RATIO})`,
              color: BRAND.parchment,
              flexShrink: 0,
              display: 'block',
              transition: 'opacity 120ms ease',
              '&:hover': { opacity: 0.75 },
            }}
          >
            <Icon name="logo:cg" size="100%" title="CommonGround" />
          </Box>
        </>
      )}
    </Box>
  );
}
